import { useMutation, useQuery } from '@tanstack/react-query'
import {
	ActionBar,
	AddIcon,
	Button,
	Checkbox,
	CloseIcon,
	InfoIcon,
	Input,
	SpinnerWithBackground,
	Text,
	Tooltip,
	TooltipContent,
	TooltipTrigger,
	UnsavedChangesDialog,
	useUnsavedChangesWarning,
} from '@viapeople/components'
import type React from 'react'
import { useEffect, useState } from 'react'
import { flushSync } from 'react-dom'
import { type Control, Controller, type FieldErrors, type FieldPath, useFieldArray, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { useToast } from '../../utils/ToastContext'
import { queryClient, trpc } from '../../utils/trpc'
import type { Role } from '../roles/types'
import { AddRolesModal } from './AddRolesModal'
import type {
	PermissionTemplate,
	PermissionTemplateEdit,
	PermissionTemplateFixedRoleDataCreate,
	PermissionTemplateFixedRoleDataEdit,
	PermissionTemplateInput,
	PermissionTemplateRoleDataCreate,
	PermissionTemplateRoleDataEdit,
} from './types'

enum PermissionTemplateFixedRoleType {
	RATER = 'rater',
	UNSOLICITED = 'unsolicited',
	PARTICIPANT = 'participant',
	PRIMARY_MANAGER = 'primary_manager',
	CO_MANAGER = 'co_manager',
	SECONDARY_MANAGER = 'secondary_manager',
	HIGH_LEVEL_MANAGER = 'high_level_manager',
	DIRECT_REPORTS = 'direct_reports',
	PEER = 'peer',
	DOWN = 'down',
	UP = 'up',
}

const FIXED_ROLE_DISPLAY_NAMES: Record<PermissionTemplateFixedRoleType, string> = {
	[PermissionTemplateFixedRoleType.RATER]: 'Rater',
	[PermissionTemplateFixedRoleType.UNSOLICITED]: 'Unsolicited',
	[PermissionTemplateFixedRoleType.PARTICIPANT]: 'Self',
	[PermissionTemplateFixedRoleType.PRIMARY_MANAGER]: 'Direct Manager',
	[PermissionTemplateFixedRoleType.CO_MANAGER]: 'Co-Manager',
	[PermissionTemplateFixedRoleType.SECONDARY_MANAGER]: 'Secondary Manager',
	[PermissionTemplateFixedRoleType.HIGH_LEVEL_MANAGER]: 'High Level Manager',
	[PermissionTemplateFixedRoleType.DIRECT_REPORTS]: 'Direct Reports',
	[PermissionTemplateFixedRoleType.PEER]: 'Peer',
	[PermissionTemplateFixedRoleType.DOWN]: 'Downward',
	[PermissionTemplateFixedRoleType.UP]: 'Upward',
}

const DEFAULT_ANONYMITY_THRESHOLD = 1

type TemplateFormData = {
	name: string
	roles: PermissionTemplateRoleDataCreate[] | PermissionTemplateRoleDataEdit[]
	fixedRoles: PermissionTemplateFixedRoleDataCreate[] | PermissionTemplateFixedRoleDataEdit[]
}

const initialFixedRolesData = {
	write: false,
	read: false,
	optional: false,
	reports: false,
	pdfView: false,
	anonymous: false,
	showGroupEvalInAcknowledge: false,
	anonimityThreshold: DEFAULT_ANONYMITY_THRESHOLD,
}

const defaultFixedRoles = [
	{
		...initialFixedRolesData,
		fixedRole: PermissionTemplateFixedRoleType.PARTICIPANT,
	},
	{
		...initialFixedRolesData,
		fixedRole: PermissionTemplateFixedRoleType.UNSOLICITED,
	},
	{
		...initialFixedRolesData,
		fixedRole: PermissionTemplateFixedRoleType.RATER,
	},
	{
		...initialFixedRolesData,
		fixedRole: PermissionTemplateFixedRoleType.PRIMARY_MANAGER,
	},
	{
		...initialFixedRolesData,
		fixedRole: PermissionTemplateFixedRoleType.CO_MANAGER,
	},
	{
		...initialFixedRolesData,
		fixedRole: PermissionTemplateFixedRoleType.SECONDARY_MANAGER,
	},
	{
		...initialFixedRolesData,
		fixedRole: PermissionTemplateFixedRoleType.HIGH_LEVEL_MANAGER,
	},
	{
		...initialFixedRolesData,
		fixedRole: PermissionTemplateFixedRoleType.DIRECT_REPORTS,
	},
	{
		...initialFixedRolesData,
		fixedRole: PermissionTemplateFixedRoleType.PEER,
	},
	{
		...initialFixedRolesData,
		fixedRole: PermissionTemplateFixedRoleType.DOWN,
	},
	{
		...initialFixedRolesData,
		fixedRole: PermissionTemplateFixedRoleType.UP,
	},
]

const TOOLTIP_CONTENT: Record<string, React.ReactNode> = {
	pdfView: (
		<>
			<div className='font-semibold text-sm text-neutral-900 mb-2'>PDF View Permission</div>
			<div className='text-xs text-neutral-600 leading-relaxed space-y-1.5'>
				<p>Controls whether form components using this permission template appear in PDF exports.</p>
				<p className='font-medium text-neutral-700 mt-2'>Key Points:</p>
				<ul className='list-disc list-inside space-y-0.5 ml-1 text-neutral-600'>
					<li>Independent of read, write, and reports permissions</li>
					<li>Users can download PDFs once they have an assigned task</li>
					<li>Available even before submitting evaluations</li>
				</ul>
			</div>
		</>
	),
	readPerm: (
		<>
			<div className='font-semibold text-sm text-neutral-900 mb-2'>Read Permission</div>
			<div className='text-xs text-neutral-600 leading-relaxed space-y-1.5'>
				<p>Controls whether a form component is displayed to the user.</p>
			</div>
		</>
	),
	writePerm: (
		<>
			<div className='font-semibold text-sm text-neutral-900 mb-2'>Write Permission</div>
			<div className='text-xs text-neutral-600 leading-relaxed space-y-1.5'>
				<p>Controls whether a form component is editable and allows the user to submit input.</p>
			</div>
		</>
	),
	optionalPerm: (
		<>
			<div className='font-semibold text-sm text-neutral-900 mb-2'>Optional Permission</div>
			<div className='text-xs text-neutral-600 leading-relaxed space-y-1.5'>
				<p>Controls whether a form component requires user input before submission.</p>
			</div>
		</>
	),
	showAckn: (
		<>
			<div className='font-semibold text-sm text-neutral-900 mb-2'>Show Group Eval in Acknowledge Permission</div>
			<div className='text-xs text-neutral-600 leading-relaxed space-y-1.5'>
				<p>Controls whether group evaluation (rater) submissions are displayed separately in the acknowledge step.</p>
			</div>
		</>
	),
	anonPerm: (
		<>
			<div className='font-semibold text-sm text-neutral-900 mb-2'>Anonymous Permission</div>
			<div className='text-xs text-neutral-600 leading-relaxed space-y-1.5'>
				<p>Controls whether submitter information is hidden when displaying feedback.</p>
			</div>
		</>
	),
	anonThresholdPerm: (
		<>
			<div className='font-semibold text-sm text-neutral-900 mb-2'>Anonymous Threshold Permission</div>
			<div className='text-xs text-neutral-600 leading-relaxed space-y-1.5'>
				<p>Minimum number of submissions required before reports are displayed.</p>
			</div>
		</>
	),
}
const HeaderCell = ({ children, tooltip }: { children: React.ReactNode; tooltip?: string }) => {
	return (
		<th className='py-2 text-left'>
			<div className='inline-flex items-center'>
				<Text variant='hint' className='text-neutral-500'>
					{children}
					{tooltip && TOOLTIP_CONTENT[tooltip] && (
						<Tooltip>
							<TooltipTrigger asChild>
								<button
									type='button'
									className='inline-flex items-center justify-center translate-y-[3px] translate-x-1'
									aria-label='More information'>
									<InfoIcon
										size={14}
										className='text-neutral-400 cursor-help hover:text-neutral-600 transition-colors'
									/>
								</button>
							</TooltipTrigger>
							<TooltipContent
								side='top'
								align='start'
								className='w-80 max-w-[min(20rem,calc(100vw-2rem))] px-4 py-3 bg-white border border-neutral-200 shadow-lg'
								sideOffset={8}>
								<div className='text-left'>{TOOLTIP_CONTENT[tooltip]}</div>
							</TooltipContent>
						</Tooltip>
					)}
				</Text>
			</div>
		</th>
	)
}

const RowCell = ({ children }: { children: React.ReactNode }) => {
	return <td className='py-3'>{children}</td>
}

const RoleRow = ({
	roleName,
	index,
	isAnonymous,
	prefix,
	rowKey,
	control,
	errors,
	fixedRole,
}: {
	roleName: string
	index: number
	isAnonymous: boolean
	prefix: 'roles' | 'fixedRoles'
	rowKey: string
	control: Control<TemplateFormData>
	errors: FieldErrors<TemplateFormData>
	fixedRole?: string
}) => {
	const writePath = `${prefix}.${index}.write` as FieldPath<TemplateFormData>
	const optionalPath = `${prefix}.${index}.optional` as FieldPath<TemplateFormData>
	const readPath = `${prefix}.${index}.read` as FieldPath<TemplateFormData>
	const reportsPath = `${prefix}.${index}.reports` as FieldPath<TemplateFormData>
	const anonymousPath = `${prefix}.${index}.anonymous` as FieldPath<TemplateFormData>
	const showGroupEvalPath = `${prefix}.${index}.showGroupEvalInAcknowledge` as FieldPath<TemplateFormData>
	const thresholdPath = `${prefix}.${index}.anonimityThreshold` as FieldPath<TemplateFormData>

	const isParticipant = fixedRole === PermissionTemplateFixedRoleType.PARTICIPANT

	const pdfViewPath = `${prefix}.${index}.pdfView` as FieldPath<TemplateFormData>

	return (
		<tr key={rowKey} className='border-b border-neutral-100 grid grid-cols-9 text-left items-center'>
			<RowCell>
				<Text variant='xsmall' className='capitalize'>
					{roleName}
				</Text>
			</RowCell>
			<RowCell>
				<Controller
					name={writePath}
					control={control}
					render={({ field }) => <Checkbox checked={field.value as boolean} onChange={field.onChange} />}
				/>
			</RowCell>
			<RowCell>
				<Controller
					name={optionalPath}
					control={control}
					render={({ field }) => <Checkbox checked={field.value as boolean} onChange={field.onChange} />}
				/>
			</RowCell>
			<RowCell>
				<Controller
					name={readPath}
					control={control}
					render={({ field }) => <Checkbox checked={field.value as boolean} onChange={field.onChange} />}
				/>
			</RowCell>
			<RowCell>
				<Controller
					name={reportsPath}
					control={control}
					render={({ field }) => <Checkbox checked={field.value as boolean} onChange={field.onChange} />}
				/>
			</RowCell>
			<RowCell>
				<Controller
					name={pdfViewPath}
					control={control}
					render={({ field }) => <Checkbox checked={field.value as boolean} onChange={field.onChange} />}
				/>
			</RowCell>
			<RowCell>
				<Controller
					name={anonymousPath}
					control={control}
					render={({ field }) => <Checkbox checked={field.value as boolean} onChange={field.onChange} />}
				/>
			</RowCell>
			<RowCell>
				{prefix === 'fixedRoles' && isParticipant ? (
					<Controller
						name={showGroupEvalPath}
						control={control}
						render={({ field }) => <Checkbox checked={field.value as boolean} onChange={field.onChange} />}
					/>
				) : (
					<span className='text-neutral-300'>—</span>
				)}
			</RowCell>
			<RowCell>
				<Controller
					name={thresholdPath}
					control={control}
					rules={isAnonymous ? { required: true, min: 1, max: 100 } : undefined}
					render={({ field }) => (
						<Input
							type='number'
							value={!isAnonymous ? '' : (field.value as number)}
							onChange={(e) => field.onChange(e.target.value)}
							disabled={!isAnonymous}
							className='h-9 text-sm'
							variant={errors[prefix]?.[index]?.anonimityThreshold ? 'error' : 'default'}
						/>
					)}
				/>
			</RowCell>
		</tr>
	)
}

const prepareRoleData = (
	role: Role,
	existingRole?: PermissionTemplateRoleDataEdit,
): PermissionTemplateRoleDataCreate => ({
	roleId: role.id,
	write: existingRole?.write || false,
	read: existingRole?.read || false,
	optional: existingRole?.optional || false,
	reports: existingRole?.reports || false,
	pdfView: existingRole?.pdfView || false,
	anonymous: existingRole?.anonymous || false,
	showGroupEvalInAcknowledge: existingRole?.showGroupEvalInAcknowledge || false,
	anonimityThreshold:
		existingRole?.anonimityThreshold !== undefined
			? existingRole.anonimityThreshold
			: existingRole?.anonymous
				? DEFAULT_ANONYMITY_THRESHOLD
				: DEFAULT_ANONYMITY_THRESHOLD,
	...(existingRole && { id: existingRole.id }),
})

export function TemplateFormPage() {
	const navigate = useNavigate()
	const { templateId, organizationId } = useParams<{
		templateId: string
		organizationId: string
	}>()
	const isEditMode = !!templateId
	const toast = useToast()

	const backToTemplatesUrl = `/organizations/${organizationId}/permission-templates`
	const [isLoading, setIsLoading] = useState(false)
	const [isRolesModalOpen, setIsRolesModalOpen] = useState(false)
	const [selectedRoles, setSelectedRoles] = useState<Role[]>([])

	const {
		handleSubmit,
		control,
		reset,
		formState: { isDirty, errors },
		watch,
	} = useForm<TemplateFormData>({
		defaultValues: {
			name: '',
			roles: [],
			fixedRoles: defaultFixedRoles,
		},
	})

	const { fields, replace } = useFieldArray({
		control,
		name: 'roles',
	})

	const { isBlocked, confirmLeave, cancelLeave } = useUnsavedChangesWarning(isDirty)

	const rolesValues = watch('roles')
	const fixedRolesValues = watch('fixedRoles')

	const templateQuery = useQuery({
		...trpc.admin.permissionTemplates.getById.queryOptions({
			id: templateId as string,
			organizationId: organizationId as string,
		}),
		enabled: !!templateId && !!organizationId,
	})

	const createTemplateMutation = useMutation(
		trpc.admin.permissionTemplates.create.mutationOptions({
			onSuccess: () => {
				toast.showToast('Template created successfully')
				flushSync(() => reset())
				navigate(backToTemplatesUrl)
			},
			onError: (error) => {
				toast.showToast(`Error creating template: ${error.message}`, 'error')
				setIsLoading(false)
			},
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.permissionTemplates.search.queryKey({
						organizationId: organizationId as string,
					}),
					exact: false,
					refetchType: 'all',
				})
			},
		}),
	)

	const editTemplateMutation = useMutation(
		trpc.admin.permissionTemplates.edit.mutationOptions({
			onSuccess: () => {
				toast.showToast('Template updated successfully')
				flushSync(() => reset())
				navigate(backToTemplatesUrl)
			},
			onError: (error) => {
				toast.showToast(`Error creating template: ${error.message}`, 'error')
				setIsLoading(false)
			},
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.permissionTemplates.search.queryKey({
						organizationId: organizationId as string,
					}),
					exact: false,
					refetchType: 'all',
				})
				queryClient.invalidateQueries({
					queryKey: trpc.admin.permissionTemplates.getById.queryKey({
						id: templateId as string,
						organizationId: organizationId as string,
					}),
					exact: false,
					refetchType: 'all',
				})
			},
		}),
	)

	useEffect(() => {
		if (isEditMode && templateQuery.data) {
			const template = templateQuery.data as unknown as PermissionTemplate
			const templateRoles = template.roles || []

			// Ensure fixedRoles are in the same order as defaultFixedRoles for consistency
			const sortedFixedRoles = defaultFixedRoles.map((defaultRole) => {
				const existingRole = template.fixedRoles?.find((fr) => fr.fixedRole === defaultRole.fixedRole)
				return existingRole || defaultRole
			})

			reset({
				name: template.name || '',
				roles: templateRoles.map((tr) => ({
					id: tr.id,
					roleId: tr.roleId,
					write: tr.write || false,
					read: tr.read || false,
					optional: tr.optional || false,
					reports: tr.reports || false,
					pdfView: tr.pdfView || false,
					anonymous: tr.anonymous || false,
					anonimityThreshold: tr.anonimityThreshold || DEFAULT_ANONYMITY_THRESHOLD,
					showGroupEvalInAcknowledge: tr.showGroupEvalInAcknowledge || false,
				})),
				fixedRoles: sortedFixedRoles.map((fr) => ({
					...('id' in fr && fr.id ? { id: fr.id } : {}),
					fixedRole: fr.fixedRole,
					write: fr.write || false,
					read: fr.read || false,
					optional: fr.optional || false,
					reports: fr.reports || false,
					pdfView: fr.pdfView || false,
					anonymous: fr.anonymous || false,
					anonimityThreshold: fr.anonimityThreshold || DEFAULT_ANONYMITY_THRESHOLD,
					showGroupEvalInAcknowledge: fr.showGroupEvalInAcknowledge || false,
				})),
			})
		} else if (!isEditMode) {
			reset({
				name: '',
				roles: [],
				fixedRoles: defaultFixedRoles,
			})
		}
	}, [templateQuery.data, isEditMode, reset])

	const prepareFormDataForSubmission = (data: TemplateFormData): TemplateFormData => {
		const processedRoles = data.roles.map((role) => ({
			...role,
			anonimityThreshold: role.anonymous ? Number(role.anonimityThreshold) : DEFAULT_ANONYMITY_THRESHOLD,
		}))

		const processedFixedRoles = data.fixedRoles.map((role) => ({
			...role,
			anonimityThreshold: role.anonymous ? Number(role.anonimityThreshold) : DEFAULT_ANONYMITY_THRESHOLD,
		}))

		return {
			name: data.name,
			roles: processedRoles,
			fixedRoles: processedFixedRoles as PermissionTemplateFixedRoleDataCreate[],
		}
	}

	const handleFormSubmit = async (data: TemplateFormData) => {
		setIsLoading(true)

		try {
			const processedData = prepareFormDataForSubmission(data)
			console.log('Submitting permission template data:', processedData)

			if (isEditMode && templateId) {
				await editTemplateMutation.mutateAsync({
					id: templateId,
					organizationId: organizationId as string,
					name: processedData.name,
					roles: processedData.roles as PermissionTemplateRoleDataEdit[],
					fixedRoles: processedData.fixedRoles || [],
				} as PermissionTemplateEdit)
			} else {
				await createTemplateMutation.mutateAsync({
					name: processedData.name,
					organizationId: organizationId as string,
					roles: processedData.roles as PermissionTemplateRoleDataCreate[],
					fixedRoles: processedData.fixedRoles || [],
				} as PermissionTemplateInput)
			}
		} catch (error) {
			console.error('Failed to save template:', error)
		}
	}

	const handleCancel = () => {
		navigate(backToTemplatesUrl)
	}

	const handleClose = () => {
		navigate(backToTemplatesUrl)
	}

	const handleAddRoles = (selectedRoles: Role[]) => {
		const currentRoles = watch('roles')
		const currentRoleIds = new Set(currentRoles.map((role) => role.roleId))

		const newRoles = selectedRoles.filter((role) => !currentRoleIds.has(role.id))
		setSelectedRoles((prev) => [...prev, ...newRoles])
		const preparedNewRoles = newRoles.map((role) => prepareRoleData(role))

		replace([...currentRoles, ...preparedNewRoles])
	}

	if (templateQuery.isLoading) {
		return <SpinnerWithBackground />
	}

	if (templateQuery.isError && isEditMode) {
		return (
			<main className='p-6'>
				<Text>Error loading template data. Please try again.</Text>
				<div className='mt-4'>
					<ActionBar
						rightActions={[
							{
								variant: 'secondary',
								label: 'Back to Templates',
								onClick: handleCancel,
							},
						]}
					/>
				</div>
			</main>
		)
	}

	return (
		<main className='h-full flex flex-col relative'>
			<div className='flex justify-between items-center px-6 py-4 mb-6 border-b border-neutral-100'>
				<Text variant='medium' weight='semibold'>
					{isEditMode ? 'Edit template' : 'Add new template'}
				</Text>
				<button type='button' onClick={handleClose} className='p-1 hover:bg-neutral-50 rounded transition-colors'>
					<CloseIcon />
				</button>
			</div>
			<form onSubmit={handleSubmit(handleFormSubmit)} className='flex flex-col flex-grow w-full'>
				<div className='p-6 flex items-start gap-2'>
					<Controller
						name='name'
						rules={{ required: 'Template name is required' }}
						control={control}
						render={({ field }) => (
							<Input
								{...field}
								placeholder='Template Name'
								label='Template Name'
								variant={errors.name ? 'error' : 'default'}
								hint={errors.name?.message as string | undefined}
							/>
						)}
					/>
					<Button
						icon={AddIcon}
						iconPosition='left'
						variant='tertiary'
						className='mt-6.5'
						onClick={() => setIsRolesModalOpen(true)}>
						Add Role
					</Button>
				</div>

				<div className='px-6'>
					<table className='w-full'>
						<thead>
							<tr className='border-b border-neutral-100 grid grid-cols-9 text-left'>
								<HeaderCell>Role</HeaderCell>
								<HeaderCell tooltip='writePerm'>Write Permissions</HeaderCell>
								<HeaderCell tooltip='optionalPerm'>Optional</HeaderCell>
								<HeaderCell tooltip='readPerm'>Read</HeaderCell>
								<HeaderCell>View Submissions</HeaderCell>
								<HeaderCell tooltip='pdfView'>PDF View</HeaderCell>
								<HeaderCell tooltip='anonPerm'>Anonymous For</HeaderCell>
								<HeaderCell tooltip='showAckn'>Show Group Eval in Acknowledge</HeaderCell>
								<HeaderCell tooltip='anonThresholdPerm'>Anonymity Threshold</HeaderCell>
							</tr>
						</thead>
						<tbody>
							{fixedRolesValues?.map((fixedRole, index) => {
								const isAnonymous = fixedRole.anonymous
								// Both create and edit types have fixedRole at runtime
								// TypeScript doesn't recognize it on edit type, so we assert it
								const fixedRoleName = (fixedRole as PermissionTemplateFixedRoleDataCreate).fixedRole

								return (
									<RoleRow
										key={`fixed-${fixedRoleName}`}
										rowKey={`fixed-${fixedRoleName}`}
										roleName={FIXED_ROLE_DISPLAY_NAMES[fixedRoleName] || fixedRoleName}
										index={index}
										isAnonymous={isAnonymous || false}
										prefix='fixedRoles'
										control={control}
										errors={errors}
										fixedRole={fixedRoleName}
									/>
								)
							})}

							{fields.map((field, index) => {
								const isAnonymous = rolesValues?.[index]?.anonymous
								const roleData = templateQuery.data?.roles?.find((r) => r.roleId === field.roleId)
								const roleName = roleData?.role?.name || selectedRoles.find((r) => r.id === field.roleId)?.name || ''

								return (
									<RoleRow
										key={field.id}
										rowKey={field.id}
										roleName={roleName}
										index={index}
										isAnonymous={isAnonymous || false}
										prefix='roles'
										control={control}
										errors={errors}
									/>
								)
							})}
						</tbody>
					</table>
				</div>

				<div className='flex-grow'></div>

				<ActionBar
					rightActions={[
						{
							variant: 'secondary',
							label: 'Cancel',
							onClick: handleCancel,
						},
						{
							variant: 'primary',
							label: isEditMode ? 'Save Changes' : 'Add template',
							onClick: handleSubmit(handleFormSubmit),
							disabled: isLoading || !isDirty,
						},
					]}
				/>
				{(isLoading || createTemplateMutation.isPending || editTemplateMutation.isPending) && <SpinnerWithBackground />}
			</form>

			<AddRolesModal
				isOpen={isRolesModalOpen}
				onClose={() => setIsRolesModalOpen(false)}
				onAddRoles={handleAddRoles}
				organizationId={organizationId as string}
				ignoredRoles={fields.map((field) => field.roleId)}
			/>
			<UnsavedChangesDialog isBlocked={isBlocked} confirmLeave={confirmLeave} cancelLeave={cancelLeave} />
		</main>
	)
}
